import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { motion } from "framer-motion";
import FocusTrap from "focus-trap-react";

import { slideInFromTop, zIndex } from "shared/styles/constants";
import { ITheme } from "shared/types";
import {
  IActionDropdownItem,
  IActionDropdownLabel,
  Overlay,
  ActionDropdownLabel,
  ActionDropdownItem,
  ActionDropdownTrigger,
  ACTION_DROPDOWN_TYPE,
} from "shared/components";
import { blurActiveElement } from "shared/utils";

import {
  StyledActionDropdownContainer,
  StyledActionDropdown,
  StyledActionDropdownCloseButton,
  StyledActionDropdownProps,
} from "./styles";

export type IActionDropdownOptions = (IActionDropdownLabel | IActionDropdownItem)[];

interface ActionDropdownProps extends StyledActionDropdownProps {
  label: string; // required for a11y
  icon?: React.ComponentType<any>;
  triggerIcon?: React.ComponentType<any>;
  options: IActionDropdownOptions;
  placement?: "bottom" | "bottom-start" | "bottom-end";
  wrapOptions?: boolean;
  dataTestId?: string;
  theme?: ITheme;
  hideContainer?: boolean;
  isActive?: boolean;
  size?: "s" | "m";
}

export const ActionDropdown: React.FC<ActionDropdownProps> = ({
  label,
  icon,
  triggerIcon,
  options,
  placement = "bottom-start",
  wrapOptions,
  dataTestId,
  theme,
  hideContainer,
  noWrap,
  isActive,
  size,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const { styles } = usePopper(referenceElement, popperElement, {
    placement,
    strategy: "fixed",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  const renderItem = useCallback(
    ({ type, ...props }: IActionDropdownItem | IActionDropdownLabel, index: number) => {
      switch (type) {
        case ACTION_DROPDOWN_TYPE.LABEL:
          return <ActionDropdownLabel key={index} type={type} {...props} />;
        case ACTION_DROPDOWN_TYPE.ITEM:
        default: {
          const { handleClick, label, ...rest } = props as IActionDropdownItem;
          return (
            <ActionDropdownItem
              key={index}
              label={label}
              handleClick={() => {
                handleClick();

                // close droddown on option click
                setIsOpen(false);

                // blur trigger
                blurActiveElement(true);
              }}
              {...rest}
              wrapOptions={wrapOptions}
            />
          );
        }
      }
    },
    [wrapOptions]
  );

  return (
    <StyledActionDropdownContainer ref={setReferenceElement} data-testid={dataTestId}>
      <ActionDropdownTrigger
        label={label}
        handleClick={() => setIsOpen(!isOpen)}
        icon={icon}
        triggerIcon={triggerIcon}
        isOpen={isOpen}
        theme={theme}
        isActive={isActive}
        hideContainer={hideContainer}
        size={size}
      />
      {isOpen && (
        <>
          {createPortal(
            <>
              {/* close on outside dropdown click */}
              <Overlay onClick={() => setIsOpen(false)} />
              <div ref={setPopperElement} style={{ zIndex: zIndex.dropdown, ...styles.popper }}>
                <FocusTrap
                  focusTrapOptions={{
                    allowOutsideClick: true,
                  }}
                >
                  <motion.div {...slideInFromTop}>
                    <StyledActionDropdown
                      noWrap={noWrap}
                      data-testid={`${dataTestId}_dropdown`}
                      overflowY="auto"
                      maxHeight="400px"
                      minWidth="200px"
                    >
                      {options.map(renderItem)}
                      <StyledActionDropdownCloseButton
                        onClick={() => setIsOpen(false)}
                        data-testid="close_action_dropdown"
                      >
                        Close
                      </StyledActionDropdownCloseButton>
                    </StyledActionDropdown>
                  </motion.div>
                </FocusTrap>
              </div>
            </>,
            document.body
          )}
        </>
      )}
    </StyledActionDropdownContainer>
  );
};
